/**
 * English: Scrollable transcript list with streaming indicator and per-mode badges.
 * Thai: รายการประวัติแชทที่เลื่อนได้ พร้อมตัวบอกสถานะ stream และ badge ตามโหมด
 */
import { useEffect, useRef, useState } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import MessageBubble, { MESSAGE_TYPE_DISPLAY } from '../tasks/MessageBubble';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import { ChatMessage } from '../../types';
import { SecondaryTab } from './ChatTabs';
import { ChatInputMode } from './ChatInput';
import { compareChatDisplayOrder } from '../../utils/chatDisplayOrder';
import { parseChatTimestampMs } from '../../utils/chatTimestamp';
import { isChatTestDeviceTarget, isChatTestMasterTarget } from '../../utils/chatTestTargets';

/** Chat row with monotonic display order assigned on the client (Chat Test page). */
export type ChatHistoryMessage = ChatMessage & { clientOrder: number };

export interface UserMessage {
  text: string;
  time: number;
  mode: string;
  clientOrder: number;
  /** Set when mode === 'employee' — the specific device agent_id this message was sent to. */
  chatTarget?: string;
}

interface ChatHistoryProps {
  history: ChatHistoryMessage[];
  userMessages: UserMessage[];
  agentColors: Record<string, string>;
  streaming: boolean;
  chatTarget: string;
  secondaryTab: SecondaryTab | null;
  inputMode?: ChatInputMode;
  /**
   * Manager + main view: show Chat + Insight + Control lanes in one scroll (Redis unified session).
   */
  unifiedManagerTranscript?: boolean;
}

interface DisplayItem {
  type: 'user' | 'agent';
  clientOrder: number;
  sortTime: number;
  userMsg?: UserMessage;
  agentMsg?: ChatHistoryMessage;
}

/**
 * Drop only exact duplicate rows (same clientOrder) — e.g. a row that appears twice after a
 * re-render. Cross-source duplicates (optimistic local copy vs persisted Redis copy of the
 * same send) are already collapsed 1:1 upstream by `mergeUserMessages`, so we must NOT dedupe
 * by content here: doing so wrongly merges genuinely repeated sends (sending the same command
 * twice) into a single bubble, making the second send appear to vanish.
 */
function dedupeUserMessages(messages: UserMessage[]): UserMessage[] {
  const out: UserMessage[] = [];
  const seenOrders = new Set<number>();
  for (const msg of messages) {
    if (seenOrders.has(msg.clientOrder)) continue;
    seenOrders.add(msg.clientOrder);
    out.push(msg);
  }
  return out;
}

/** ข้อความในสาย Master → ผลสรุปสุดท้าย */
function isMasterMainMessage(m: ChatMessage): boolean {
  return m.message_type === 'master_routing' || m.message_type === 'master_final_result';
}

/** ข้อความในสาย Manager Chat — รวม confirm */
function isManagerMainMessage(m: ChatMessage): boolean {
  if (m.agent_id === 'manager' || m.agent_id === 'confirm' || m.agent_id === 'system') return true;
  return (m.message_type || '').startsWith('confirm_');
}

/** Discussion ระหว่าง Manager สถานีต่างๆ (Master mode) */
function isMasterDiscussion(m: ChatMessage): boolean {
  return m.message_type === 'manager_contribution' || m.message_type === 'manager_discussion';
}

/** ข้อมูลระดับสถานีย่อย (forwarded จาก station sub-graph) */
function isStationDetails(m: ChatMessage): boolean {
  return m.agent_id.includes('/');
}

/** contribution + discussion ของ device agents ใน station */
function isTeamDiscussion(m: ChatMessage): boolean {
  return m.message_type === 'contribution' || m.message_type === 'discussion';
}

/** ข้อความจาก Chat mode (direct LLM) */
function isChatDirectMessage(m: ChatMessage): boolean {
  return m.agent_id === 'assistant' || m.agent_id === 'system';
}

function isToolMessage(m: ChatMessage): boolean {
  return false;
}

/** Short label for user bubble (Redis lane / live send). */
function userModeBadgeLabel(mode: string, showChatBadge: boolean): string | null {
  if (mode === 'chat' || mode === 'insight' || mode === 'ask' || mode === 'insights') return 'ถาม (Ask)';
  if (mode === 'control') return 'Command';
  if (mode === 'master') return 'Master';
  return mode || null;
}

/** Chat direct LLM + Manager graph main line (Insight/Control/confirm) — one scroll for unified session. */
function isChatOrManagerMain(m: ChatMessage): boolean {
  return isChatDirectMessage(m) || isManagerMainMessage(m);
}

/** User bubbles to show for the current route + input mode (same lane as filterMessages). */
export function filterUserMessagesForView(
  messages: UserMessage[],
  inputMode: ChatInputMode | undefined,
  chatTarget: string,
  unifiedManagerTranscript?: boolean,
): UserMessage[] {
  if (isChatTestMasterTarget(chatTarget)) {
    return messages.filter((m) => !m.mode || m.mode === 'master');
  }
  if (isChatTestDeviceTarget(chatTarget)) {
    return messages.filter(
      (m) =>
        (!m.mode || m.mode === 'employee' || m.mode === 'control') &&
        m.chatTarget === chatTarget,
    );
  }
  // manager lane (chat / insight / control) — exclude master and employee rows
  return messages.filter(
    (m) => !m.mode || (m.mode !== 'master' && m.mode !== 'employee'),
  );
}

/**
 * Returns true when a message is compatible with the current view lane.
 * Messages stamped with a `mode` field are gated to their lane; unstamped
 * messages (live WS frames, legacy rows) fall through to the existing
 * agent_id / message_type predicates for backward compatibility.
 */
function inLane(m: ChatHistoryMessage, ...modes: string[]): boolean {
  const msgMode = m.mode as string | undefined;
  return !msgMode || modes.includes(msgMode);
}

/** `device_{station}_{device_id}` → device_id tail (e.g. ej-1). */
function deviceIdTailFromEmployeeAgentId(agentId: string): string | null {
  if (!agentId.startsWith('device_')) return null;
  const rest = agentId.slice('device_'.length);
  const idx = rest.indexOf('_');
  if (idx === -1 || idx >= rest.length - 1) return null;
  return rest.slice(idx + 1);
}

/**
 * Confirm rows are global agent_id "confirm" — gate them to the selected device so
 * ej-1 control does not appear on ej-2 (topic/content carries the real device).
 */
function confirmMessageBelongsToDeviceAgent(m: ChatHistoryMessage, chatTarget: string): boolean {
  const text = (m.content || '').toLowerCase();
  const target = chatTarget.toLowerCase();
  if (text.includes(target)) return true;
  const tail = deviceIdTailFromEmployeeAgentId(chatTarget);
  if (!tail) return false;
  const escaped = tail.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Path tail …/adder/ej-1, underscore form, or bare "ej-1"; boundaries prevent ej-1 ≠ ej-10.
  return new RegExp(`(^|[^a-z0-9_-])${escaped}(?![a-z0-9_-])|(?:/|_)${escaped}(?:\\b|/|$)`, 'i').test(text);
}

export function filterMessages(
  messages: ChatHistoryMessage[],
  chatTarget: string,
  secondaryTab: SecondaryTab | null,
  inputMode?: ChatInputMode,
  unifiedManagerTranscript?: boolean,
): ChatHistoryMessage[] {
  // Manager main view — user + manager synthesis ONLY.
  // Contribution / discussion messages live in the "Team Discussion" tab so the
  // main chat panel stays clean and devices conversation is grouped together.
  if (
    unifiedManagerTranscript &&
    chatTarget === 'manager' &&
    secondaryTab === null
  ) {
    return messages.filter(
      (m) => isToolMessage(m) || (inLane(m, 'chat', 'ask', 'insight', 'insights', 'control') && isChatOrManagerMain(m)),
    );
  }
  // Device agent — target-specific lane must win over the global Chat tab.
  // Otherwise a selected device can render assistant chat_response rows from Manager chat.
  if (isChatTestDeviceTarget(chatTarget)) {
    return messages.filter((m) => {
      if (!inLane(m, 'employee', 'control')) return false;
      if (m.agent_id === chatTarget) return true;
      if (m.agent_id === 'system' && m.message_type === 'employee_direct') return true;
      if (m.agent_id === 'confirm' || (m.message_type || '').startsWith('confirm_')) {
        return confirmMessageBelongsToDeviceAgent(m, chatTarget);
      }
      return false;
    });
  }
  // Chat mode — show only assistant/system messages
  if (inputMode === 'ask') {
    return messages.filter((m) => inLane(m, 'chat', 'ask', 'insight', 'insights') && isChatDirectMessage(m));
  }
  if (isChatTestMasterTarget(chatTarget)) {
    if (secondaryTab === 'discussion')
      return messages.filter((m) => inLane(m, 'master') && isMasterDiscussion(m));
    if (secondaryTab === 'stations')
      return messages.filter((m) => inLane(m, 'master') && isStationDetails(m));
    return messages.filter((m) => inLane(m, 'master') && isMasterMainMessage(m));
  }
  if (chatTarget === 'manager') {
    if (secondaryTab === 'teamDiscussion') {
      // Team Discussion tab: เฉพาะ contribution / discussion ของ device agents เท่านั้น
      // ไม่รวม Manager summary, confirm, หรือ user bubble
      return messages.filter(
        (m) => inLane(m, 'ask', 'insight', 'insights', 'control') && isTeamDiscussion(m),
      );
    }
    if (secondaryTab === 'personalities') return messages; // handled outside (device personalities grid)
    return messages.filter(
      (m) => inLane(m, 'ask', 'insight', 'insights', 'control') && isManagerMainMessage(m),
    );
  }
  return [];
}

const PAGE = 100;

export default function ChatHistory({
  history,
  userMessages,
  agentColors,
  streaming,
  chatTarget,
  secondaryTab,
  inputMode,
  unifiedManagerTranscript = false,
}: ChatHistoryProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const prevLenRef = useRef(0);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, userMessages, streaming]);

  // Reset window when messages are cleared (e.g. handleClear)
  useEffect(() => {
    const totalItems = history.length + userMessages.length;
    if (totalItems < prevLenRef.current) setVisibleCount(PAGE);
    prevLenRef.current = totalItems;
  }, [history.length, userMessages.length]);

  const isEmpty = history.length === 0 && userMessages.length === 0;

  if (isEmpty && !streaming) {
    const emptyHint =
      inputMode === 'ask'
        ? 'คุยกับผู้ช่วย — พิมพ์ด้านล่าง · Chat with the assistant — type below'
        : chatTarget === 'master'
          ? 'Send a message to talk with Master Agent'
          : chatTarget === 'manager'
            ? 'Send a message to talk with Manager'
            : 'Send a message to talk directly with this device';
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState text={emptyHint} icon={ChatBubbleLeftRightIcon} />
      </div>
    );
  }

  const filtered = filterMessages(history, chatTarget, secondaryTab, inputMode, unifiedManagerTranscript);
  const userMessagesForView = dedupeUserMessages(
    filterUserMessagesForView(
      userMessages,
      inputMode,
      chatTarget,
      unifiedManagerTranscript,
    ),
  );
  const showChatLaneBadge =
    unifiedManagerTranscript &&
    chatTarget === 'manager' &&
    (secondaryTab === null || secondaryTab === 'teamDiscussion');

  // Team Discussion is a focused view of device agent exchanges — drop user bubbles
  // (and Manager summary) so it reads as a pure A2A transcript.
  const showUserMessages = secondaryTab === null;

  const items: DisplayItem[] = [
    ...(showUserMessages
      ? userMessagesForView.map((msg) => ({
          type: 'user' as const,
          clientOrder: msg.clientOrder,
          sortTime: msg.time,
          userMsg: msg,
        }))
      : []),
    ...filtered.map((msg) => {
      const tb = parseChatTimestampMs(msg.timestamp);
      return {
        type: 'agent' as const,
        clientOrder: msg.clientOrder,
        sortTime: Number.isFinite(tb) ? tb : NaN,
        agentMsg: msg,
      };
    }),
  ].sort((a, b) => {
    const aTime = Number.isFinite(a.sortTime) ? a.sortTime : 0;
    const bTime = Number.isFinite(b.sortTime) ? b.sortTime : 0;
    const primary = compareChatDisplayOrder(
      { clientOrder: a.clientOrder, sortTimeMs: aTime },
      { clientOrder: b.clientOrder, sortTimeMs: bTime },
    );
    if (primary !== 0) return primary;
    if (a.type !== b.type) return a.type === 'user' ? -1 : 1;
    return 0;
  });

  const visibleItems = items.slice(-visibleCount);
  const hiddenCount = items.length - visibleItems.length;

  if (items.length === 0 && (history.length > 0 || userMessages.length > 0) && !streaming) {
    const hint =
      secondaryTab === 'discussion'
        ? 'ยังไม่มี Manager Discussion ในการสนทนานี้'
        : secondaryTab === 'stations'
          ? 'ยังไม่มีข้อมูลสถานีย่อย'
          : secondaryTab === 'teamDiscussion'
            ? 'ยังไม่มี contribution / discussion จาก device agents'
            : isChatTestDeviceTarget(chatTarget)
              ? 'ยังไม่มีคำตอบจาก device นี้'
              : 'No messages to show for this view.';
    return (
      <div className="flex-1 flex w-full items-center justify-center px-4 min-h-0">
        <EmptyState text={hint} icon={ChatBubbleLeftRightIcon} />
      </div>
    );
  }

  const teamDiscussionSummary =
    chatTarget === 'manager' && secondaryTab === 'teamDiscussion' && filtered.length > 0
      ? {
          contribution: filtered.filter((m) => m.message_type === 'contribution').length,
          discussion: filtered.filter((m) => m.message_type === 'discussion').length,
        }
      : null;

  return (
    <div className="flex-1 overflow-y-auto px-4 min-h-0">
      {hiddenCount > 0 && (
        <button
          onClick={() => setVisibleCount((c) => c + PAGE)}
          className="sticky top-0 z-10 w-full py-2 text-xs text-center text-app-accent hover:text-app-accent/80 bg-app-surface/90 backdrop-blur-sm border-y border-app-border transition-colors"
        >
          ↑ {hiddenCount} earlier messages — click to load more
        </button>
      )}
      {teamDiscussionSummary && (
        <div className="sticky top-0 z-[9] flex flex-wrap items-center gap-2 py-2 mb-1 border-b border-app-border bg-app-surface/95 backdrop-blur-sm -mx-4 px-4">
          <span className="text-[10px] font-medium text-app-text-dim shrink-0">สรุปในมุมมองนี้:</span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-app-muted text-app-text border border-app-border">
            {MESSAGE_TYPE_DISPLAY.contribution ?? 'Contribution'} · {teamDiscussionSummary.contribution}
          </span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-app-muted text-app-text border border-app-border">
            {MESSAGE_TYPE_DISPLAY.discussion ?? 'Discussion'} · {teamDiscussionSummary.discussion}
          </span>
        </div>
      )}
      <div className="space-y-0.5 py-2">
        {visibleItems.map((item) => {
          if (item.type === 'user' && item.userMsg) {
            const laneLabel = userModeBadgeLabel(item.userMsg.mode, showChatLaneBadge);
            return (
              <div key={`user-${item.userMsg.clientOrder}`} className="flex justify-end py-2">
                <div className="max-w-[72%]">
                  <div className="flex items-center gap-2 mb-1 justify-end flex-wrap">
                    <span className="text-sm font-semibold text-app-user">You</span>
                    {laneLabel && (
                      <span className="text-[10px] font-medium text-app-text-dim border border-app-border rounded px-1.5 py-0.5">
                        {laneLabel}
                      </span>
                    )}
                    <span className="text-[11px] text-app-text-dim">
                      {new Date(item.userMsg.time).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="bg-app-accent/20 text-app-text text-sm rounded-xl rounded-tr-sm px-4 py-2.5 leading-relaxed whitespace-pre-wrap shadow-sm">
                    {item.userMsg.text}
                  </div>
                </div>
              </div>
            );
          }
          if (item.agentMsg) {
            return (
              <MessageBubble
                key={`agent-${item.agentMsg.clientOrder}_${item.agentMsg.agent_id}`}
                message={item.agentMsg}
                agentColor={agentColors[item.agentMsg.agent_id]}
              />
            );
          }
          return null;
        })}
      </div>
      {streaming && (
        <div className="py-4 flex justify-center">
          <LoadingSpinner size="sm" />
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
